import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getCourses, getExam, submitExam } from "@/services/api";
import Layout from "@/components/Layout";
import { Timer, ChevronLeft, CheckCircle2, XCircle, Award } from "lucide-react";

function formatTime(seconds) {
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function ExamPage() {
  const { courseId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const decodedId = decodeURIComponent(courseId);

  const [exam, setExam] = useState(null);
  const [assignmentId, setAssignmentId] = useState(null);
  const [courseName, setCourseName] = useState("");
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitError, setSubmitError] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [eRes, cRes] = await Promise.all([
          getExam(token, decodedId),
          getCourses(token, user.id),
        ]);
        setExam(eRes.data);
        setTimeLeft((eRes.data.time_limit_minutes || 30) * 60);
        setAnswers(new Array((eRes.data.questions || []).length).fill(-1));
        const course = (cRes.data || []).find((c) => c.course_identifier === decodedId);
        if (course) {
          setAssignmentId(course.assignment_id);
          setCourseName(course.course_name);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [courseId, token, user, decodedId]);

  const handleSubmit = useCallback(async (timeOut = false) => {
    clearInterval(timerRef.current);
    const finalAnswers = answers.map((a) => (a === -1 ? 0 : a));
    setSubmitted(true);
    setSubmitError(false);
    try {
      const res = await submitExam(token, decodedId, {
        user_id: user.id,
        course_identifier: decodedId,
        assignment_id: assignmentId || 0,
        course_name: courseName || decodedId,
        answers: finalAnswers,
      });
      setResult(res.data);
    } catch (e) {
      console.error(e);
      setSubmitError(true);
      setSubmitted(false);
    }
  }, [answers, decodedId, token, user.id, assignmentId, courseName]);

  // Timer
  useEffect(() => {
    if (started && !submitted) {
      timerRef.current = setInterval(() => {
        setTimeLeft((t) => {
          if (t <= 1) {
            clearInterval(timerRef.current);
            handleSubmit(true);
            return 0;
          }
          return t - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [started, submitted, handleSubmit]);

  const handleAnswer = (qIdx, optIdx) => {
    if (submitted) return;
    setAnswers((prev) => {
      const copy = [...prev];
      copy[qIdx] = optIdx;
      return copy;
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-64" style={{ color: "#242220" }}>
          <p className="font-bold opacity-40">Vizsga betöltése...</p>
        </div>
      </Layout>
    );
  }

  // Pre-exam screen
  if (!started) {
    return (
      <Layout>
        <div data-testid="exam-intro-page">
          <button
            onClick={() => navigate(`/courses/${courseId}`)}
            className="flex items-center gap-2 mb-6 font-bold text-sm"
            style={{ color: "#242220", background: "none", border: "none", cursor: "pointer" }}
          >
            <ChevronLeft size={18} strokeWidth={2.5} />
            Vissza
          </button>

          <div
            className="max-w-lg mx-auto p-8 text-center"
            style={{
              background: "#fff",
              borderRadius: "24px",
              border: "1.5px solid #242220",
              boxShadow: "8px 8px 0 #242220",
            }}
          >
            <div
              className="w-16 h-16 mx-auto mb-6 flex items-center justify-center"
              style={{
                background: "#FF9456",
                borderRadius: "20px",
                border: "1.5px solid #242220",
              }}
            >
              <Award size={30} strokeWidth={2.5} color="#242220" />
            </div>
            <h1 className="text-2xl font-bold mb-3" style={{ color: "#242220" }}>
              Záróvizsga
            </h1>
            <p className="text-base mb-6" style={{ color: "#242220", opacity: 0.7 }}>
              {courseName}
            </p>

            <div className="grid grid-cols-3 gap-3 mb-8">
              {[
                { label: "Kérdések", value: exam?.questions?.length || 0 },
                { label: "Időlimit", value: `${exam?.time_limit_minutes || 30} perc` },
                { label: "Minimális követelmény", value: `${exam?.pass_score || 70}%` },
              ].map((s) => (
                <div
                  key={s.label}
                  className="p-3"
                  style={{
                    background: "#FDF5EB",
                    borderRadius: "16px",
                    border: "1.5px solid #242220",
                  }}
                >
                  <p className="text-lg font-bold" style={{ color: "#242220" }}>{s.value}</p>
                  <p className="text-xs" style={{ color: "#242220", opacity: 0.6 }}>{s.label}</p>
                </div>
              ))}
            </div>

            <p className="text-sm mb-6" style={{ color: "#242220", opacity: 0.6 }}>
              A vizsga elindítása után a visszaszámlálás megkezdődik. Minden kérdésre válaszolj,
              mielőtt beküldöd az eredményt.
            </p>

            <button
              onClick={() => setStarted(true)}
              className="w-full py-3 font-bold text-base transition-all hover:translate-x-[1px] hover:translate-y-[1px] active:translate-x-[3px] active:translate-y-[3px]"
              style={{
                background: "#FBD556",
                borderRadius: "30px",
                border: "1.5px solid #242220",
                boxShadow: "4px 4px 0 #242220",
                color: "#242220",
                cursor: "pointer",
              }}
              data-testid="start-exam-btn"
            >
              Záróvizsga indítása
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // Results screen
  if (submitted && result) {
    return (
      <Layout>
        <div
          className="max-w-lg mx-auto p-8 text-center"
          style={{
            background: "#fff",
            borderRadius: "24px",
            border: "1.5px solid #242220",
            boxShadow: "8px 8px 0 #242220",
            marginTop: "2rem",
          }}
          data-testid="exam-result-page"
        >
          <div
            className="w-20 h-20 mx-auto mb-6 flex items-center justify-center"
            style={{
              background: result.passed ? "#A1E1B9" : "#FDECEA",
              borderRadius: "24px",
              border: "1.5px solid #242220",
            }}
          >
            {result.passed ? (
              <CheckCircle2 size={36} strokeWidth={2.5} color="#242220" />
            ) : (
              <XCircle size={36} strokeWidth={2.5} color="#D32F2F" />
            )}
          </div>

          <h1 className="text-2xl font-bold mb-2" style={{ color: "#242220" }}>
            {result.passed ? "Sikeres vizsga!" : "Sikertelen vizsga"}
          </h1>
          <p className="text-sm mb-6" style={{ color: "#242220", opacity: 0.6 }}>
            {result.passed
              ? "Gratulálunk! Teljesítetted a kurzust."
              : "Sajnos nem sikerült most. Próbáld újra!"}
          </p>

          <div
            className="text-5xl font-bold mb-2"
            style={{ color: result.passed ? "#242220" : "#D32F2F" }}
            data-testid="exam-score"
          >
            {result.score}%
          </div>
          <p className="text-sm mb-8" style={{ color: "#242220", opacity: 0.6 }}>
            {result.correct_count} / {result.total} helyes válasz
          </p>

          {result.certificate_number && (
            <div
              className="p-4 mb-6"
              style={{
                background: "#A1E1B9",
                borderRadius: "16px",
                border: "1.5px solid #242220",
              }}
            >
              <p className="text-xs font-bold" style={{ color: "#242220", opacity: 0.7 }}>
                Bizonyítvány száma
              </p>
              <p className="text-base font-bold" style={{ color: "#242220" }}>
                {result.certificate_number}
              </p>
            </div>
          )}

          <div className="flex flex-col gap-3">
            {result.passed ? (
              <button
                onClick={() => navigate(`/courses/${courseId}/certificate`)}
                className="w-full py-3 font-bold transition-all active:translate-x-[2px] active:translate-y-[2px]"
                style={{
                  background: "#FBD556",
                  borderRadius: "30px",
                  border: "1.5px solid #242220",
                  boxShadow: "4px 4px 0 #242220",
                  color: "#242220",
                  cursor: "pointer",
                }}
                data-testid="go-to-certificate-btn"
              >
                Bizonyítvány megtekintése
              </button>
            ) : (
              <button
                onClick={() => {
                  setStarted(false);
                  setSubmitted(false);
                  setResult(null);
                  setSubmitError(false);
                  setAnswers(new Array((exam?.questions || []).length).fill(-1));
                  setTimeLeft((exam?.time_limit_minutes || 30) * 60);
                }}
                className="w-full py-3 font-bold transition-all active:translate-x-[2px] active:translate-y-[2px]"
                style={{
                  background: "#FF9456",
                  borderRadius: "30px",
                  border: "1.5px solid #242220",
                  boxShadow: "4px 4px 0 #242220",
                  color: "#242220",
                  cursor: "pointer",
                }}
                data-testid="retry-exam-btn"
              >
                Újra próbálom
              </button>
            )}
            <button
              onClick={() => navigate(`/courses/${courseId}`)}
              className="w-full py-3 font-bold text-sm"
              style={{
                background: "transparent",
                borderRadius: "30px",
                border: "1.5px solid #242220",
                color: "#242220",
                cursor: "pointer",
              }}
              data-testid="back-to-course-from-exam-btn"
            >
              Vissza a kurzushoz
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // Exam questions
  const unanswered = answers.filter((a) => a === -1).length;

  return (
    <Layout>
      <div data-testid="exam-questions-page">
        {/* Timer bar */}
        <div
          className="sticky top-0 z-20 flex items-center justify-between px-6 py-3 mb-6"
          style={{
            background: "#fff",
            borderRadius: "16px",
            border: "1.5px solid #242220",
            boxShadow: "3px 3px 0 #242220",
          }}
        >
          <p className="font-bold text-sm" style={{ color: "#242220" }}>
            Záróvizsga: {courseName}
          </p>
          <div
            className="flex items-center gap-2 px-4 py-2"
            style={{
              background: timeLeft < 300 ? "#FDECEA" : "#FBD556",
              borderRadius: "20px",
              border: "1.5px solid #242220",
            }}
            data-testid="exam-timer"
          >
            <Timer size={16} strokeWidth={2.5} color="#242220" />
            <span className="font-bold text-sm" style={{ color: timeLeft < 300 ? "#D32F2F" : "#242220" }}>
              {formatTime(timeLeft)}
            </span>
          </div>
        </div>

        {submitError && (
          <div
            className="flex flex-col items-start gap-3 p-4 mb-6"
            style={{
              background: "#FDECEA",
              border: "1.5px solid #D32F2F",
              borderRadius: "16px",
            }}
            data-testid="exam-submit-error"
          >
            <p className="text-sm font-bold" style={{ color: "#D32F2F" }}>
              A vizsga beküldése nem sikerült. A vizsga adatai időközben frissülhettek – töltsd újra az oldalt, majd próbáld újra.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 text-sm font-bold transition-all active:translate-x-[2px] active:translate-y-[2px]"
              style={{
                background: "#FF9456",
                borderRadius: "20px",
                border: "1.5px solid #242220",
                boxShadow: "3px 3px 0 #242220",
                color: "#242220",
                cursor: "pointer",
              }}
              data-testid="exam-reload-btn"
            >
              Oldal újratöltése
            </button>
          </div>
        )}

        <div className="max-w-2xl mx-auto">
          <div className="space-y-8">
            {(exam?.questions || []).map((q, qi) => (
              <div
                key={q.id}
                className="p-6"
                style={{
                  background: "#fff",
                  borderRadius: "20px",
                  border: "1.5px solid #242220",
                  boxShadow: "4px 4px 0 #242220",
                }}
                data-testid={`exam-question-${qi}`}
              >
                {q.image ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left column - Question and answers */}
                    <div>
                      <p className="font-bold text-base mb-4" style={{ color: "#242220" }}>
                        {qi + 1}. {q.question}
                      </p>
                      <div className="space-y-2">
                        {q.options.map((opt, oi) => (
                          <button
                            key={oi}
                            onClick={() => handleAnswer(qi, oi)}
                            className="w-full text-left px-4 py-3 text-sm font-bold transition-all"
                            style={{
                              background: answers[qi] === oi ? "#FBD556" : "#FDF5EB",
                              borderRadius: "14px",
                              border: "1.5px solid #242220",
                              boxShadow: answers[qi] === oi ? "2px 2px 0 #242220" : "none",
                              color: "#242220",
                              cursor: "pointer",
                            }}
                            data-testid={`exam-option-${qi}-${oi}`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Right column - Image */}
                    <div className="flex items-start">
                      <img
                        src={q.image}
                        alt=""
                        className="w-full object-contain"
                        style={{
                          maxHeight: 300,
                          borderRadius: "16px",
                          border: "1.5px solid #242220",
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="font-bold text-base mb-4" style={{ color: "#242220" }}>
                      {qi + 1}. {q.question}
                    </p>
                    <div className="space-y-2">
                      {q.options.map((opt, oi) => (
                        <button
                          key={oi}
                          onClick={() => handleAnswer(qi, oi)}
                          className="w-full text-left px-4 py-3 text-sm font-bold transition-all"
                          style={{
                            background: answers[qi] === oi ? "#FBD556" : "#FDF5EB",
                            borderRadius: "14px",
                            border: "1.5px solid #242220",
                            boxShadow: answers[qi] === oi ? "2px 2px 0 #242220" : "none",
                            color: "#242220",
                            cursor: "pointer",
                          }}
                          data-testid={`exam-option-${qi}-${oi}`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 pb-8">
            {unanswered > 0 && (
              <p className="text-center text-sm mb-4 font-bold" style={{ color: "#FF9456" }}>
                {unanswered} kérdés még megválaszolatlan
              </p>
            )}
            <button
              onClick={() => handleSubmit(false)}
              className="w-full py-4 font-bold text-base transition-all hover:translate-x-[1px] hover:translate-y-[1px] active:translate-x-[3px] active:translate-y-[3px]"
              style={{
                background: "#FBD556",
                borderRadius: "30px",
                border: "1.5px solid #242220",
                boxShadow: "6px 6px 0 #242220",
                color: "#242220",
                cursor: "pointer",
              }}
              data-testid="submit-exam-btn"
            >
              Vizsga beküldése
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
